import type { Types } from '@amplitude/analytics-browser'

import type { AmplitudeErrorDetails, AmplitudeErrorReporter } from './config'

const PREFIX = '[Amplitude]'

const toText = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (value instanceof Error) return value.message
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    return String(value)
  }
}

export const consoleReporter: AmplitudeErrorReporter = (message, details) => {
  // eslint-disable-next-line no-console
  const log = details?.level === 'error' ? console.error : console.warn
  log(PREFIX, message, details)
}

/** Delivery failures repeat on every flush; one report per message and page load is the signal. */
export const reportOnce = (report: AmplitudeErrorReporter): AmplitudeErrorReporter => {
  const reported = new Set<string>()

  return (message, details) => {
    if (reported.has(message)) return
    reported.add(message)
    report(message, details)
  }
}

export const createLogger = (report: AmplitudeErrorReporter): Types.ILogger => {
  const forward = (level: AmplitudeErrorDetails['level']) => {
    return (...args: Array<unknown>) => {
      const message = args.map(toText).join(' ')
      report(message, { level, args })
    }
  }

  return {
    disable: () => undefined,
    enable: () => undefined,
    log: () => undefined,
    debug: () => undefined,
    warn: forward('warn'),
    error: forward('error'),
  }
}
